/**
 * Pure helpers shared by the daemon's progress probe and the viewer's progress model:
 * classify a shell command as a check, match it against the landing recipe, and parse test
 * counts from common runners' output. No zod, so UI bundles can import it cheaply via
 * `@viko/protocol/progress-parse`.
 */
const norm = (s) => s.replace(/\s+/g, ' ').trim();
/**
 * The command a login-shell wrapper runs (`/bin/zsh -lc '…'`, `bash -lc "…"`, `sh -c '…'`, as
 * Codex reports every exec). Only a single, fully quoted argument without escapes is unwrapped;
 * anything else is returned unchanged (and the classifier's metacharacter rules then apply).
 */
export function unwrapShell(command) {
    const m = /^\s*(?:\/\S*\/)?(?:ba|z|da|k)?sh -l?c (?:'([^']*)'|"([^"\\$`]*)")\s*$/.exec(command);
    return m ? (m[1] ?? m[2] ?? '') : command;
}
const PATTERNS = [
    [
        'test',
        /\b(vitest|jest|mocha|ava|pytest|playwright test|cargo (nextest|test)|go test|swift test|dotnet test|xcodebuild\b[^|;&]*\btest|bun test|deno test|rspec|phpunit|ctest|(pnpm|npm|yarn|bun)( run)?( -r| --filter[= ]\S+| -F \S+)* (test|t|test:\S+|e2e|verify)\b|turbo (run )?test|make (test|check)|node --test|node (?:\S+\/)?(?:tests?|\S+\.(?:test|spec))\.[cm]?[jt]s$)\b/,
    ],
    [
        'typecheck',
        /\b(tsc\b|tsgo\b|typecheck|type-check|mypy|pyright|cargo check|vue-tsc|flow check)\b/,
    ],
    ['lint', /\b(biome (check|lint|ci)|eslint|ruff|clippy|swiftlint|golangci-lint|stylelint|lint)\b/],
    [
        'build',
        /\b(vite build|next build|cargo build|go build|xcodebuild|gradle\S* (assemble|build)|dotnet build|swift build|tsup|esbuild|(pnpm|npm|yarn|bun)( run)?( -r| --filter[= ]\S+| -F \S+)* build\b|turbo (run )?build|make\b(?! (test|check)))/,
    ],
];
/**
 * Quote-aware split into commands and their following operator. Returns null for anything whose
 * exit code cannot be attributed: substitutions, variables, backgrounding, subshells, heredocs,
 * escapes or redirections other than `2>&1` / `2>/dev/null`.
 */
function shellSegments(command) {
    const out = [];
    let cur = '';
    let quote = null;
    const push = (op) => {
        const text = norm(cur);
        if (!text)
            return false;
        out.push(op ? { text, op } : { text });
        cur = '';
        return true;
    };
    for (let i = 0; i < command.length; i++) {
        const ch = command[i];
        if (quote) {
            if (ch === quote)
                quote = null;
            else if (quote === '"' && (ch === '$' || ch === '`' || ch === '\\'))
                return null;
            cur += ch;
            continue;
        }
        if (ch === "'" || ch === '"') {
            quote = ch;
            cur += ch;
            continue;
        }
        if (ch === '2' && /(^|\s)$/.test(cur)) {
            const m = /^2>(?:&1|\/dev\/null)(?=\s|$|[|;&])/.exec(command.slice(i));
            if (m) {
                i += m[0].length - 1;
                continue;
            }
        }
        if (/[\n\r`$<>\\(){}]/.test(ch))
            return null;
        const next = command[i + 1];
        if (ch === '&') {
            if (next !== '&' || !push('&&'))
                return null;
            i++;
        }
        else if (ch === '|') {
            if (!push(next === '|' ? '||' : '|'))
                return null;
            if (next === '|')
                i++;
        }
        else if (ch === ';') {
            if (!push(';'))
                return null;
        }
        else
            cur += ch;
    }
    if (quote || !push())
        return null;
    return out;
}
/** Output filters a check may be piped through (its output, not its exit code, survives). */
const FILTER = /^(?:tail|head|grep|egrep|fgrep|rg|sed|awk|cat|tee|cut|sort|uniq|wc|tr|column|less|more)\b/;
/** Filters that never fail on their own and never close the pipe early (safe under pipefail). */
const PIPEFAIL_SAFE = /^(?:tail|cat|tee|cut|sort|uniq|wc|tr|column)\b/;
const CD = /^cd\s+(?:[^\s'"]+|"[^"]*"|'[^']*')$/;
const PIPEFAIL = /^set\s+(?:-[a-z]*o\s+pipefail|-o\s+pipefail(?:\s+-[a-z]+)?)$/;
const EXPORT = /^export\s+[A-Za-z_]\w*=[^\s'"]*$/;
const ENV_PREFIX = /^(?:env\s+)?(?:[A-Za-z_]\w*=[^\s'"]*\s+)*/;
/**
 * Classify a shell command as a check after stripping the plumbing agents wrap around it:
 * login-shell wrappers, `cd … &&`, `set -o pipefail`, `export`/env assignments, `2>&1`, and
 * output filters (`| tail -3`, `| grep …`). `exit` says whether the reported exit status is the
 * check's own. Null when the command is not a check or its result cannot be attributed to one.
 */
export function analyzeCheckCommand(command, recipe) {
    const segs = shellSegments(unwrapShell(command));
    if (!segs)
        return null;
    let i = 0;
    let pipefail = false;
    for (; i < segs.length - 1; i++) {
        const s = segs[i];
        if (CD.test(s.text) && s.op === '&&')
            continue;
        if (EXPORT.test(s.text) && (s.op === '&&' || s.op === ';'))
            continue;
        if (PIPEFAIL.test(s.text) && (s.op === '&&' || s.op === ';')) {
            pipefail = true;
            continue;
        }
        break;
    }
    const check = segs[i];
    const filters = segs.slice(i + 1);
    if (check.op && check.op !== '|')
        return null;
    for (const [n, f] of filters.entries()) {
        if (!FILTER.test(f.text))
            return null;
        if (n < filters.length - 1 ? f.op !== '|' : f.op != null)
            return null;
    }
    const c = check.text.replace(ENV_PREFIX, '');
    if (!c || /['"]/.test(c))
        return null;
    const kind = classifySingle(c, recipe);
    if (!kind)
        return null;
    const exact = !filters.length || (pipefail && filters.every((f) => PIPEFAIL_SAFE.test(f.text)));
    return { ...kind, exit: exact ? 'direct' : 'piped' };
}
/**
 * What kind of check a shell command is, or null when it is not a check whose exit code is its
 * own (see `analyzeCheckCommand` for piped checks). A command that matches one of the project's
 * recipe commands is always a check (its kind comes from the recipe list it is in, else from the
 * patterns).
 */
export function classifyCommand(command, recipe) {
    const a = analyzeCheckCommand(command, recipe);
    if (a?.exit !== 'direct')
        return null;
    return a.recipeStep ? { kind: a.kind, recipeStep: a.recipeStep } : { kind: a.kind };
}
function classifySingle(c, recipe) {
    if (!/^(?:(?:npx|pnpm exec|npm exec|yarn exec) )?(?:pnpm|npm|yarn|bun|vitest|jest|mocha|ava|pytest|python(?:3)? -m pytest|playwright|cargo|go|swift|dotnet|xcodebuild|deno|rspec|phpunit|ctest|turbo|make|tsc|tsgo|typecheck|mypy|pyright|vue-tsc|flow|biome|node|eslint|ruff|clippy|swiftlint|golangci-lint|stylelint|vite|next|gradle|tsup|esbuild)\b/.test(c) &&
        !recipeStep(c, recipe))
        return null;
    // Pure reads of files or listings are never checks, even if a path mentions "test".
    if (/^(cat|less|head|tail|ls|grep|rg|find|sed -n|echo|git (log|show|diff|status))\b/.test(c))
        return null;
    const step = recipeStep(c, recipe);
    for (const [kind, re] of PATTERNS) {
        if (re.test(c))
            return step ? { kind, recipeStep: step } : { kind };
    }
    if (step)
        return {
            kind: recipe?.build?.some((r) => norm(r) === step) ? 'build' : 'test',
            recipeStep: step,
        };
    return null;
}
/** The recipe command `command` runs (exact or as part of a `&&` chain), if any. */
export function recipeStep(command, recipe) {
    command = unwrapShell(command);
    const c = norm(command);
    if (/[;|`$<>\n\r]/.test(command))
        return undefined;
    const parts = c.split(/\s*&&\s*/).map((p) => p.replace(/^cd \S+$/, '').trim());
    if (parts.length > 2 || (parts.length === 2 && parts[0] !== ''))
        return undefined;
    for (const r of [...(recipe?.test ?? []), ...(recipe?.build ?? [])]) {
        const n = norm(r);
        if (n && (c === n || parts.includes(n)))
            return n;
    }
    return undefined;
}
// biome-ignore lint/suspicious/noControlCharactersInRegex: ANSI escape sequences
const ANSI = /\u001b\[[0-9;?]*[ -/]*[@-~]/g;
/**
 * Parse pass/fail counts from a test runner's output. Recognizes vitest, jest, pytest, cargo
 * test, go test, mocha, node --test and playwright summaries. Returns {} when nothing matched.
 * When a runner prints several summaries (e.g. a workspace), counts are summed.
 */
export function parseTestCounts(output) {
    if (!output)
        return {};
    const text = output.replace(ANSI, '');
    const out = {};
    const add = (k, n) => {
        out[k] = (out[k] ?? 0) + n;
    };
    let matched = false;
    // vitest: "Tests  1 failed | 37 passed (38)" / "Tests  38 passed (38)", optionally behind a
    // turbo task prefix ("@viko/web:test:       Tests  28 passed (28)").
    let vitestTotal = true;
    for (const m of text.matchAll(/^(?:\S+:\s+)?\s*Tests\s+((?:\d+\s+(?:passed|failed|skipped|todo)\s*\|?\s*)+)(?:\((\d+)\))?\s*$/gm)) {
        matched = true;
        const body = m[1] ?? '';
        for (const p of body.matchAll(/(\d+)\s+(passed|failed|skipped|todo)/g)) {
            const n = Number(p[1]);
            if (p[2] === 'passed')
                add('passed', n);
            else if (p[2] === 'failed')
                add('failed', n);
            else
                add('skipped', n);
        }
        if (m[2])
            add('total', Number(m[2]));
        else
            vitestTotal = false;
    }
    if (!vitestTotal)
        delete out.total;
    // jest: "Tests:       1 failed, 37 passed, 38 total"
    if (!matched) {
        for (const m of text.matchAll(/^\s*Tests:\s+(.+?)(\d+) total\s*$/gm)) {
            matched = true;
            for (const p of (m[1] ?? '').matchAll(/(\d+)\s+(passed|failed|skipped|todo|pending)/g)) {
                const n = Number(p[1]);
                if (p[2] === 'passed')
                    add('passed', n);
                else if (p[2] === 'failed')
                    add('failed', n);
                else
                    add('skipped', n);
            }
            add('total', Number(m[2]));
        }
    }
    // cargo: "test result: ok. 38 passed; 0 failed; 1 ignored;"
    if (!matched) {
        for (const m of text.matchAll(/test result: \w+\. (\d+) passed; (\d+) failed;(?: (\d+) ignored;)?/g)) {
            matched = true;
            add('passed', Number(m[1]));
            add('failed', Number(m[2]));
            if (m[3])
                add('skipped', Number(m[3]));
        }
    }
    // pytest: "===== 2 failed, 36 passed, 1 skipped in 3.21s ====="
    if (!matched) {
        const m = text.match(/=+ (.*?(?:passed|failed|error)[^=]*?) in [\d.]+s\S* =+/);
        if (m) {
            matched = true;
            for (const p of (m[1] ?? '').matchAll(/(\d+) (passed|failed|skipped|errors?|xfailed)/g)) {
                const n = Number(p[1]);
                if (p[2] === 'passed')
                    add('passed', n);
                else if (p[2] === 'failed' || p[2]?.startsWith('error'))
                    add('failed', n);
                else
                    add('skipped', n);
            }
        }
    }
    // mocha / node --test / playwright: "38 passing" "1 failing" / "# pass 38" "# fail 1" / "38 passed"
    if (!matched) {
        const pass = text.match(/^\s*(\d+) passing\b/m) ?? text.match(/^# pass (\d+)/m);
        const fail = text.match(/^\s*(\d+) failing\b/m) ?? text.match(/^# fail (\d+)/m);
        const pw = text.match(/^\s*(\d+) passed \(/m);
        const pwFail = text.match(/^\s*(\d+) failed$/m);
        if (pass || fail) {
            matched = true;
            if (pass)
                add('passed', Number(pass[1]));
            if (fail)
                add('failed', Number(fail[1]));
        }
        else if (pw) {
            matched = true;
            add('passed', Number(pw[1]));
            if (pwFail)
                add('failed', Number(pwFail[1]));
        }
    }
    // go test: count "--- PASS" / "--- FAIL" lines
    if (!matched && /^(ok|FAIL)\s+\S+/m.test(text)) {
        const p = text.match(/^\s*--- PASS/gm)?.length ?? 0;
        const f = text.match(/^\s*--- FAIL/gm)?.length ?? 0;
        if (p || f) {
            matched = true;
            add('passed', p);
            add('failed', f);
        }
    }
    // turbo (e.g. the tail of `pnpm verify`): " Tasks:    28 successful, 28 total"
    if (!matched) {
        const m = text.match(/^\s*Tasks:\s+(\d+) successful, (\d+) total\s*$/m);
        if (m) {
            const ok = Number(m[1]);
            const total = Number(m[2]);
            return { passed: ok, failed: Math.max(0, total - ok), total, unit: 'tasks' };
        }
    }
    if (!matched)
        return {};
    if (out.total == null && (out.passed != null || out.failed != null))
        out.total = (out.passed ?? 0) + (out.failed ?? 0) + (out.skipped ?? 0);
    const failing = failingNames(text);
    if (failing.length)
        out.failing = failing;
    return out;
}
/** Failing test/file names from common runners (capped at 8). */
export function failingNames(text) {
    const names = new Set();
    const clean = text.replace(ANSI, '');
    for (const m of clean.matchAll(/^\s*(?:×|✗|✕|FAIL)\s+(.+?)\s*(?:\d+ms)?$/gm)) {
        const n = (m[1] ?? '').replace(/\s+\[.*$/, '').trim();
        if (n && !/^\d+ tests?/.test(n))
            names.add(n.slice(0, 160));
        if (names.size >= 8)
            break;
    }
    if (!names.size) {
        for (const m of clean.matchAll(/^FAILED (\S+)/gm)) {
            names.add(m[1] ?? '');
            if (names.size >= 8)
                break;
        }
    }
    return [...names].filter(Boolean);
}
//# sourceMappingURL=progress-parse.js.map